import React from 'react';
import { useDispatch } from 'react-redux';

import Media, { MediaInterface, UploadStatus } from 'types/Media';
import UploadMediaRequest from 'modules/medias/types/UploadMediaRequest';
import { NotificationType } from 'modules/notifications/types/Notification';
import MediaUploadUrlRequest from 'modules/medias/types/MediaUploadUrlRequest';

import { addNotification } from 'modules/notifications/actions';

import {
  useDeleteMediaMutation, useGetMediaUrlMutation, useUploadMediaMutation,
} from 'modules/medias/service';

import useBrowse, { UseBrowseProps } from './useBrowse';
import { DEFAULT_UPDATE_ERROR } from 'constants/messages';

export interface UseUploadData {
  medias: MediaInterface[];
  upload: (params: Omit<MediaUploadUrlRequest, 'id'> & Pick<UploadMediaRequest, 'file'> & { isSignature?: boolean }) => Promise<MediaInterface | null>;
  browse: ReturnType<typeof useBrowse>;
  remove: (uploadId: string, { deleteOnServer } ?: { deleteOnServer?: boolean }) => Promise<void>;
  isDeleting: boolean;
  isLoading: boolean;
  isError: boolean;
  clearMedias: () => void;
}

interface UseUploadProps {
  selfServiceId: string;
  initialMedias?: Array<MediaInterface>;
  limit?: UseBrowseProps['limit'];
}

const useUpload = ({ selfServiceId, initialMedias = [], limit }: UseUploadProps): UseUploadData => {
  const dispatch = useDispatch();

  const [medias, setMedias] = React.useState<MediaInterface[]>(initialMedias);

  const browse = useBrowse({
    limit: limit - medias.length,
    absoluteLimit: limit,
  });

  const [getUrl, { isLoading: isUrlLoading, isError: isUrlError }] = useGetMediaUrlMutation({ fixedCacheKey: selfServiceId });
  const [uploadMedia, { isLoading: isUploadLoading, isError: isUploadError }] = useUploadMediaMutation({ fixedCacheKey: selfServiceId });
  const [deleteMedia, { isLoading: isDeleteLoading, isError: isDeleteError }] = useDeleteMediaMutation({ fixedCacheKey: selfServiceId });

  const upload = React.useCallback(
    async (
      params: Omit<MediaUploadUrlRequest, 'id'> & Pick<UploadMediaRequest, 'file'> & { isSignature?: boolean },
    ) => {
      const media = new Media({
        ...params, path: URL.createObjectURL(params.file), status: UploadStatus.UPLOADING,
      });

      try {
        setMedias((prevMedias) => [...prevMedias, media]);

        const data = await getUrl({ id: selfServiceId, ...params }).unwrap();
        const {
          uploadId, url, fields, filename, type, path
        } = data;

        await uploadMedia({ url, fields, file: params.file }).unwrap();

        setMedias((prevMedias) => prevMedias.map((prevMedia) => {
          if (prevMedia === media) {
            return new Media({
              ...prevMedia,
              ...media,
              filename,
              uploadId,
              type,
              path: path ?? prevMedia.path,
              status: UploadStatus.UPLOADED,
            });
          }

          return prevMedia;
        }));

        return new Media({
          ...media, uploadId, type, path: path ?? media.path, status: UploadStatus.UPLOADED,
        });
      } catch {
        setMedias((prevMedias) => prevMedias.filter((prevMedia) => prevMedia !== media));

        dispatch(addNotification({
          title: {
            id: 'error.media.upload.description',
            defaultMessage: 'An error occurred while uploading the media. Please try again.',
          },
          ...DEFAULT_UPDATE_ERROR,
          type: NotificationType.ERROR,
        }));
      }

      return null;
    },
    [dispatch, selfServiceId, getUrl, uploadMedia],
  );

  const remove = React.useCallback(async (uploadId: string, { deleteOnServer }: { deleteOnServer?: boolean } = {}) => {
    try {
      if (deleteOnServer) {
        setMedias((prevMedias) => prevMedias.map((media) => (
          media.uploadId === uploadId || media.id === uploadId
            ? new Media({ ...media, status: UploadStatus.DELETING })
            : media
        )));

        await deleteMedia({ selfServiceId, id: uploadId }).unwrap();
      }

      setMedias((prevMedias) => prevMedias.filter(({ uploadId: mediaId, id }) => mediaId !== uploadId && id !== uploadId));
    } catch { /* Do nothing */ }
  }, [deleteMedia, selfServiceId]);

  /**
   * @description Empties the `medias` array.
   *
   * ⚠️ It doesn't delete the media files from the server. ⚠️
   */
  const clearMedias = React.useCallback(() => {
    setMedias([]);
  }, [setMedias]);

  return React.useMemo(() => ({
    medias,
    upload,
    browse,
    remove,
    clearMedias,
    isDeleting: isDeleteLoading,
    isLoading: isUrlLoading || isUploadLoading,
    isError: isUrlError || isUploadError || isDeleteError,
  }), [
    medias,
    browse,
    upload,
    remove,
    isUrlError,
    clearMedias,
    isUploadError,
    isDeleteError,
    isDeleteLoading,
    isUrlLoading,
    isUploadLoading,
  ]);
};

export default useUpload;
